import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Banner } from "../../../_components/Contacts/ContactShared";
import FinanceDocList from "../../../_components/Finance/FinanceDocList";
import {
    decideApproval,
    getFinanceApprovalList,
    isFinalApprovalDecision,
} from "../../../_services/approval.service";
import {
    FiClock,
    FiUser,
    FiDollarSign,
    FiHome,
    FiCheckCircle,
    FiXCircle,
    FiFileText
} from "react-icons/fi";
import { T, fmtMYR } from "../../../_components/Sales/SalesShared";
import { DocStatusBadge as FinanceStatusBadge } from "../../../_components/Finance/FinanceShared";

const APPROVAL_STATUSES = ["All", "Pending for Approval", "Approved", "Rejected"];

const FINANCE_SEARCH_OPTIONS = [
    { key: "all", label: "All" },
    { key: "id", label: "Doc No." },
    { key: "date", label: "Date" },
    { key: "supplier", label: "Company Name" },
    { key: "contactPerson", label: "Contact Person" }
];

const FINANCE_TYPE_META = {
    purchase_invoice: {
        label: "Purchase Invoice",
        basePath: (item) => item.piType === "Direct"
            ? "/finance/direct-purchase-invoices"
            : "/finance/indirect-purchase-invoices",
    },
    payment_voucher: {
        label: "Payment Voucher",
        basePath: () => "/finance/payment-vouchers",
    },
    purchase_credit_note: {
        label: "Purchase Credit Note",
        basePath: () => "/finance/purchase-credit-notes",
    },
    purchase_debit_note: {
        label: "Purchase Debit Note",
        basePath: () => "/finance/purchase-debit-notes",
    },
    sales_credit_note: {
        label: "Sales Credit Note",
        basePath: () => "/finance/credit-notes",
    },
    sales_debit_note: {
        label: "Sales Debit Note",
        basePath: () => "/finance/debit-notes",
    },
};

export default function FinanceApprovals() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [refreshKey, setRefreshKey] = useState(0);
    const [banner, setBanner] = useState({ type: "", msg: "" });
    const [decidingKeys, setDecidingKeys] = useState(() => new Set());
    const bannerTimerRef = useRef(null);
    const decisionInFlightRef = useRef(new Set());

    const decisionKey = (item) => `${item.recordType}:${item.dbId}`;

    const showBanner = useCallback((type, msg) => {
        clearTimeout(bannerTimerRef.current);
        setBanner({ type, msg });
        bannerTimerRef.current = setTimeout(
            () => setBanner({ type: "", msg: "" }),
            4000
        );
    }, []);

    useEffect(() => () => clearTimeout(bannerTimerRef.current), []);

    useEffect(() => {
        if (!searchParams.get("status")) {
            setSearchParams(
                {
                    status: "Pending for Approval",
                    page: "1"
                },
                { replace: true }
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getFinanceApprovals = useCallback(
        async (qp, options = {}) => {
            void refreshKey;
            const result = await getFinanceApprovalList(qp, options);
            return {
                ...result,
                data: (result.data || []).map((item) => {
                    const meta = FINANCE_TYPE_META[item.typeKey] || {};
                    const invoiceLabel = item.typeKey === "purchase_invoice"
                        ? `${item.piType || "Indirect"} Purchase Invoice`
                        : null;
                    return {
                        ...item,
                        _docType: invoiceLabel || meta.label || item.typeKey,
                        _basePath: meta.basePath ? meta.basePath(item) : "/finance",
                        _typeKey: item.typeKey,
                    };
                }),
            };
        },
        [refreshKey]
    );

    const handleApproveRejectFinance = useCallback(
        async (item, action, onClose, reason = null) => {
            const key = decisionKey(item);
            if (decisionInFlightRef.current.has(key)) return false;
            decisionInFlightRef.current.add(key);
            setDecidingKeys((current) => new Set(current).add(key));
            try {
                const result = await decideApproval(item.recordType, item.dbId, action, reason);

                onClose();
                setRefreshKey((k) => k + 1);
                window.dispatchEvent(new CustomEvent("refreshPendingApprovalCounts"));
                const finalApproval = action === "approve" && isFinalApprovalDecision(result);
                showBanner(
                    "success",
                    action === "reject"
                        ? `Document ${item.id} rejected successfully.`
                        : finalApproval
                            ? `Document ${item.id} approved successfully.`
                            : `Approval for document ${item.id} recorded. Pending the next approval level.`
                );
                return true;
            } catch (err) {
                console.error(err);
                showBanner("danger", err.message || `Failed to process document ${item.id}.`);
                return false;
            } finally {
                decisionInFlightRef.current.delete(key);
                setDecidingKeys((current) => {
                    const next = new Set(current);
                    next.delete(key);
                    return next;
                });
            }
        },
        [showBanner]
    );

    const renderFinancePreviewActions = useCallback(
        (item, onClose, onView) => {
            return (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%" }}>
                    {item.status === "Pending for Approval" && (item.canApprove || item.canReject) && (
                        <div style={{ display: "flex", gap: 10, width: "100%" }}>
                            {item.canApprove && <button
                                disabled={decidingKeys.has(decisionKey(item))}
                                onClick={() => handleApproveRejectFinance(item, "approve", onClose)}
                                style={{
                                    flex: 1,
                                    padding: "9px 12px",
                                    background: T.success,
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 8,
                                    fontWeight: 600,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 6,
                                    cursor: "pointer"
                                }}
                            >
                                <FiCheckCircle size={14} /> Approve
                            </button>}
                            {item.canReject && <button
                                disabled={decidingKeys.has(decisionKey(item))}
                                onClick={() => {
                                    const reason = window.prompt("Please provide a reason for rejection:");
                                    if (reason === null) return;
                                    if (!reason.trim()) {
                                        alert("Rejection reason is required.");
                                        return;
                                    }
                                    handleApproveRejectFinance(item, "reject", onClose, reason);
                                }}
                                style={{
                                    flex: 1,
                                    padding: "9px 12px",
                                    background: "#fff",
                                    color: T.danger,
                                    border: `1.5px solid ${T.danger}`,
                                    borderRadius: 8,
                                    fontWeight: 600,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 6,
                                    cursor: "pointer"
                                }}
                            >
                                <FiXCircle size={14} /> Reject
                            </button>}
                        </div>
                    )}

                    <button
                        className="cl-preview-btn-primary"
                        onClick={() => onView(`${item._basePath}/${item.id}?fromApprovals=true`)}
                        style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            padding: "12px 16px",
                            fontSize: 14
                        }}
                    >
                        <FiFileText size={16} /> View Document Details
                    </button>
                </div>
            );
        },
        [decidingKeys, handleApproveRejectFinance]
    );

    const financeColumns = useMemo(
        () => [
            {
                label: "Doc. No.",
                key: "id",
                width: 110,
                render: (v) => (
                    <span style={{ color: T.textLight, fontWeight: 700, fontSize: 13 }}>{v}</span>
                )
            },
            {
                label: "Type",
                key: "_docType",
                width: 160,
                render: (v) => (
                    <span style={{ fontWeight: 600, color: T.accent, fontSize: 13 }}>{v}</span>
                )
            },
            {
                label: "Date",
                key: "date",
                width: 110,
                render: (v) => <span style={{ color: T.textMuted, fontSize: 13 }}>{v}</span>
            },
            {
                label: "Company Name",
                key: "party",
                width: 200,
                render: (v) => (
                    <span
                        style={{
                            fontWeight: 600,
                            display: "inline-block",
                            maxWidth: 190,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                        }}
                    >
                        {v}
                    </span>
                )
            },
            {
                label: "Amount",
                key: "grandTotal",
                render: (v, row) => (
                    <span style={{ fontWeight: 700, color: T.textPrimary }}>
                        {fmtMYR(v ?? row.payAmount)}
                    </span>
                )
            },
            { label: "Status", key: "status", render: (v) => <FinanceStatusBadge value={v} /> }
        ],
        []
    );

    const financePreviewFields = useMemo(
        () => [
            { icon: FiFileText, label: "Document Type", key: "_docType" },
            { icon: FiClock, label: "Date", key: "date" },
            { icon: FiHome, label: "Company Name", key: "party" },
            { icon: FiUser, label: "Contact Person", key: "contactPerson" },
            { icon: FiDollarSign, label: "Amount", key: "grandTotal", render: (v, row) => fmtMYR(v ?? row.payAmount) }
        ],
        []
    );

    return (
        <div style={{ padding: "0 20px" }}>
            <Banner type={banner.type} msg={banner.msg} onClose={() => setBanner({ type: "", msg: "" })} />

            <div style={{ marginBottom: 20 }}>
                <h4 style={{ fontWeight: 700, color: T.textPrimary, margin: 0, fontSize: 24 }}>
                    Finance & Accounting Approvals
                </h4>
                <p style={{ fontSize: 14, color: T.textMuted, margin: "4px 0 0" }}>
                    Review and process finance documents: Purchase Invoices, Payment Vouchers, Debit & Credit Notes.
                </p>
            </div>

            <FinanceDocList
                docType="Finance Document"
                basePath="/accounting"
                fetchList={getFinanceApprovals}
                statuses={APPROVAL_STATUSES}
                customColumns={financeColumns}
                renderPreviewActions={renderFinancePreviewActions}
                hideCreate={true}
                hideExport={true}
                searchOptions={FINANCE_SEARCH_OPTIONS}
                previewFields={financePreviewFields}
            />
        </div>
    );
}